"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBasketShopping,
    faShoppingBasket,
} from "@fortawesome/free-solid-svg-icons";
import Footer from "./Footer";
import PrayerRequestModal from "./PrayerRequestModal";
import { listenToCartEvents, CART_EVENTS } from "../utils/cartEvents";
import { getBrowserId } from "../utils/browserUtils";

// Custom SVG Icons as components (exact same as Upload page)
const PencilIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-edit">
        <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"></path>
        <polygon points="18 2 22 6 12 16 8 16 8 12 18 2"></polygon>
    </svg>
);

const UploadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-upload">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="17 8 12 3 7 8"></polyline>
        <line x1="12" y1="3" x2="12" y2="15"></line>
    </svg>

);

const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-download">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="7 10 12 15 17 10"></polyline>
        <line x1="12" y1="15" x2="12" y2="3"></line>
    </svg>
);

const PaymentIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-edit">
        <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"></path>
        <polygon points="18 2 22 6 12 16 8 16 8 12 18 2"></polygon>
    </svg>
);

const HomeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-home">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
);

const MenuIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
         className="feather feather-align-left status_toggle middle sidebar-toggle">
        <line x1="17" y1="10" x2="3" y2="10"></line>
        <line x1="21" y1="6" x2="3" y2="6"></line>
        <line x1="21" y1="14" x2="3" y2="14"></line>
        <line x1="17" y1="18" x2="3" y2="18"></line>
    </svg>
);

const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path
            d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
    </svg>
);

const ArrowLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z"/>
    </svg>
);

const ShoppingCartIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-shopping-bag">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <path d="M16 10a4 4 0 0 1-8 0"></path>
    </svg>
);

const LogOutIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-log-out">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
        <polyline points="16 17 21 12 16 7"></polyline>
        <line x1="21" y1="12" x2="9" y2="12"></line>
    </svg>
);

const ChevronRightIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"/>
    </svg>
);

const Layout = ({
                    children,
                    activeMenuItem = "Home",
                    breadcrumb,
                    sidebarCollapsed,
                    setSidebarCollapsed
                }) => {
    const [isMobile, setIsMobile] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true); // Sidebar open by default
    const [cartCount, setCartCount] = useState(0);
    const [showPrayerModal, setShowPrayerModal] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            // On mobile, sidebar should be closed by default
            // On desktop, sidebar should be open by default
            if (mobile) {
                setSidebarOpen(false);
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Fetch cart count - Smart API URL detection
    const getApiUrl = () => {
        if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
        const hostname = window.location.hostname;
        if (hostname === '18.133.181.177' || hostname.includes('18.133.181.177')) {
            return 'http://18.133.181.177:3001/api';
        }
        return 'http://localhost:3001/api';
    };
    const API_URL = getApiUrl();
    const fetchCartCount = async () => {
        try {
            console.debug('Fetching cart count from public endpoint');
            const browserId = getBrowserId();
            const token = localStorage.getItem('temple_token');
            
            // Include authorization token and browser_id
            const headers = {
                'x-browser-id': browserId
            };
            
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            
            const resp = await fetch(`${API_URL}/offerings/basket/count?browser_id=${browserId}`, {
                headers: headers
            });

            if (!resp.ok) {
                console.debug('Cart count fetch failed, status:', resp.status);
                return;
            }

            const json = await resp.json();
            console.debug('Cart count API response:', json);

            if (json.success && json.data && typeof json.data.item_count === 'number') {
                console.debug('Setting cart count to:', json.data.item_count);
                setCartCount(json.data.item_count);
            } else {
                console.debug('Invalid cart count response format:', json);
            }
        } catch (e) {
            console.error('Basket count fetch error', e);
        }
    };

    useEffect(() => {
        // Initial fetch
        fetchCartCount();
        
        // Set up interval for periodic updates
        const interval = setInterval(fetchCartCount, 30000); // refresh every 30s
        
        // Set up event listeners for real-time updates
        const removeCartAddedListener = listenToCartEvents(CART_EVENTS.ITEM_ADDED, () => {
            console.debug('Cart item added event received');
            fetchCartCount();
        });
        
        const removeCartRemovedListener = listenToCartEvents(CART_EVENTS.ITEM_REMOVED, () => {
            console.debug('Cart item removed event received');
            fetchCartCount();
        });
        
        const removeCartUpdatedListener = listenToCartEvents(CART_EVENTS.CART_UPDATED, () => {
            console.debug('Cart updated event received');
            fetchCartCount();
        });
        
        const removeCheckoutListener = listenToCartEvents(CART_EVENTS.CHECKOUT_COMPLETED, () => {
            console.debug('Checkout completed event received');
            setCartCount(0); // Reset cart count immediately after checkout
        });
        
        // Clean up all listeners and intervals
        return () => {
            clearInterval(interval);
            removeCartAddedListener();
            removeCartRemovedListener();
            removeCartUpdatedListener();
            removeCheckoutListener();
        };
    }, []);

    const menuItems = [
        { name: "Home", icon: HomeIcon, hasSubmenu: true },
        { name: "Online Prayer Requests", icon: PencilIcon, hasSubmenu: true },
        { name: "Upload Prayer Forms", icon: UploadIcon, hasSubmenu: false },
        { name: "Download Prayer Forms", icon: DownloadIcon, hasSubmenu: true },
        { name: "Payments & Offerings", icon: PaymentIcon, hasSubmenu: false },
        { name: "My Basket", icon: ShoppingCartIcon, hasSubmenu: false },
        { name: "Sign Out", icon: LogOutIcon, hasSubmenu: false },
    ];

    const handleMenuClick = (item) => {
        if (isMobile) setSidebarOpen(false);
        if (item.name === "My Basket") {
            navigate("/basket");
        } else if (item.name === "Home") {
            navigate("/");
        } else if (item.name === "Sign Out") {
            navigate("/login");
        } else if (item.name === "Online Prayer Requests") {
            setShowPrayerModal(true);
        } else if (item.name === "Upload Prayer Forms") {
            navigate("/upload");
        } else if (item.name === "Download Prayer Forms") {
            navigate("/download-forms");
        } else if (item.name === "Payments & Offerings") {
            navigate("/offerings");
        }
    };

    const styles = {
        container: {
            display: "flex",
            minHeight: "100vh",
            backgroundColor: "#f5f5f5",
            fontFamily: "Work, sans-serif",
            position: "relative",
            width: "100%",
        },
        header: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "80px",
            backgroundColor: "#555b6e",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px",
            zIndex: 1000,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        },
        logo: {
            fontSize: "2rem",
            fontFamily: "cursive",
            color: "white",
            fontWeight: "300",
        },
        headerLeft: {
            display: "flex",
            alignItems: "center",
            gap: "20px",
        },
        headerRight: {
            display: "flex",
            alignItems: "center",
            gap: "15px",
        },
        menuButton: {
            background: "none",
            border: "none",
            color: "white",
            fontSize: "1.5rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            padding: "8px",
        },
        goBackButton: {
            background: "none",
            border: "none",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            fontSize: "1rem",
            padding: "8px 12px",
        },
        cartIcon: {
            position: "relative",
            color: "white",
            fontSize: "1.5rem",
            cursor: "pointer",
            padding: "8px",
        },
        cartBadge: {
            position: "absolute",
            top: "0px",
            right: "0px",
            backgroundColor: "#89b0af",
            color: "white",
            borderRadius: "50%",
            width: "15px",
            height: "15px",
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        signOutButton: {
            backgroundColor: "#89b0ae",
            color: "white",
            border: "none",
            padding: "8px 16px",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "0.9rem",
        },
        sidebar: {
            paddingTop: "80px",
            paddingLeft: "10px",
            minHeight: "100vh",
            position: "fixed",
            width: sidebarOpen ? (isMobile ? "80%" : "250px") : "60px", // Collapsed width for icons
            left: 0,
            transition: "width 0.3s ease",
            zIndex: 999,
            overflowY: "auto",
            background: "linear-gradient(180deg, rgba(247,248,247,1) 0%, rgba(193,228,220,1) 100%)",
        },
        menuItem: {
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarOpen ? "space-between" : "center", // Center icons when collapsed
            padding: sidebarOpen ? "14px 20px" : "14px 0", // Adjust padding when collapsed
            color: "#ffffff",
            cursor: "pointer",
            fontSize: "0.95rem",
            transition: "background-color 0.2s, transform 0.1s ease-in-out, padding 0.3s ease",
            borderTopLeftRadius: "30px",
            borderBottomLeftRadius: "30px",
            marginBottom: "20px",
            gap: sidebarOpen ? "12px" : "0", // Remove gap when collapsed
            backgroundColor: "#89b0ae",
            fontWeight: "500",
            borderBottom: "none",
            width: "100%",
        },
        menuItemActive: {
            backgroundColor: "#4b5563",
        },
        menuItemLeft: {
            display: "flex",
            alignItems: "center",
            gap: sidebarOpen ? "12px" : "0", // Remove gap when collapsed
        },
        menuItemText: {
            display: sidebarOpen ? "block" : "none", // Hide text when collapsed
        },
        mainContent: {
            marginLeft: isMobile ? "0" : (sidebarOpen ? "250px" : "60px"),
            paddingTop: "80px",
            flex: 1,
            // Add extra bottom padding to avoid overlap with fixed footer
            padding: isMobile ? "0 0 80px" : "0 0 100px",
            width: isMobile ? "100%" : (sidebarOpen ? "calc(100% - 250px)" : "calc(100% - 60px)"),
            transition: "margin-left 0.3s ease, width 0.3s ease",
        },
        breadcrumbBar: {
            padding: isMobile ? "12px 16px" : "14px 40px",
            position: "relative",
            top: 80,
            zIndex: 5,
            borderBottom: "3px solid rgba(0,0,0,0.06)",
            marginBottom: "20px",
        },
        breadcrumb: {
            display: "flex",
            alignItems: "center",
            color: "#6b7280",
            fontSize: "0.85rem",
            gap: "8px",
            fontWeight: 800,
        },
        contentInner: {
            padding: isMobile ? "24px 20px 0" : "32px 40px 0",
        },
        headerDivider: {
            width: "1px",
            height: "100%", // full header height
            alignSelf: 'stretch',
            background: "#000000",
            margin: "0 20px 0 20px",
        },
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <header style={styles.header}>
                <div style={styles.headerLeft}>
                    <img
                        src="/logo.png"
                        alt="Shinnyo Logo"
                        style={{
                            height: "60px",
                            objectFit: "contain",
                            display: isMobile && sidebarOpen ? "none" : "block",
                        }}
                    />
                    {!isMobile && <div style={styles.headerDivider} />}
                    <button
                        style={styles.menuButton}
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                        {sidebarOpen ? <MenuIcon /> : <MenuIcon />}
                    </button>
                    {!isMobile && (
                        <button
                            style={styles.goBackButton}
                            onClick={() => window.history.back()}
                        >
                            <ArrowLeftIcon />
                            Go Back
                        </button>
                    )}
                </div>
                <div style={styles.headerRight}>
                    <div style={styles.cartIcon} onClick={() => navigate("/basket")}>
                        <FontAwesomeIcon
                            icon={faShoppingBasket}
                            style={{ marginLeft: "8px" }}
                        />
                        <span style={styles.cartBadge}>{cartCount}</span>
                    </div>
                    <button
                        style={styles.signOutButton}
                        onClick={() => navigate("/login")}
                    >
                        Sign Out
                    </button>
                </div>
            </header>

            {/* Sidebar Overlay */}
            {isMobile && sidebarOpen && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 998,
                    }}
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <nav style={styles.sidebar}>
                {menuItems.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                        <div
                            key={index}
                            style={{
                                ...styles.menuItem,
                                ...(index === 0 ? { marginTop: '70px' } : {}),
                                ...(activeMenuItem === item.name ? styles.menuItemActive : {}),
                            }}
                            onClick={() => handleMenuClick(item)}
                            onMouseEnter={(e) => {
                                if (activeMenuItem !== item.name) {
                                    e.currentTarget.style.backgroundColor = "#4b5563";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (activeMenuItem !== item.name) {
                                    e.currentTarget.style.backgroundColor = "#89b0ae";
                                }
                            }}
                            title={item.name}
                        >
                            <div style={styles.menuItemLeft}>
                                <IconComponent />
                                <span style={styles.menuItemText}>{item.name}</span>
                            </div>
                            {sidebarOpen && item.hasSubmenu && <ChevronRightIcon />}
                        </div>
                    );
                })}
            </nav>

            {/* Main Content */}
            <main style={styles.mainContent}>
                {breadcrumb && (
                    <div style={styles.breadcrumbBar}>
                        <div style={styles.breadcrumb}>{breadcrumb}</div>
                    </div>
                )}
                <div style={styles.contentInner}>
                    {children}
                </div>
                <Footer />
            </main>

            {/* Prayer Request Modal */}
            <PrayerRequestModal 
                isOpen={showPrayerModal} 
                onClose={() => setShowPrayerModal(false)} 
            />
        </div>
    );
};

export default Layout;